import React, { ChangeEvent, useState } from "react";
import AppBar from "../components/app-bar";
import CustomInput from "../components/customs/custom-input";
import CustomRadioButton from "../components/customs/custom-radio-button";
import CustomSelect from "../components/customs/custom-select";
import CustomButton from "../components/customs/custom-button";
import {
  FormSection,
  FormField,
  RadioField,
} from "../components/customs/form-section";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDebounceCallback } from "usehooks-ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import {
  branddata,
  loanStatusData,
} from "../components/chase-list-components/chase-list-data";

const CreateChase: React.FC = () => {
  const [values, setValues] = useState<Record<string, any>>({
    totalSellingPrice: 100,
    paymentType: "cash",
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const createChase = async () => {
    if (validateChaseForm()) {
      setLoading(true);
      try {
        await axiosInstance.post("/chase", {
          ...values,
        });
        toast.success("Chase created successfully");
        navigate("/chaselist");
        setLoading(false);
      } catch (error) {
        toast.error(ParseErrors(error));
        setLoading(false);
        console.log(error);
      }
    }
  };

  const HandleChangeValues = useDebounceCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({
        ...prev,
        [e.target.name]:
          e.target.type === "number" ? e.target.valueAsNumber : e.target.value,
      }));
    },
    200
  );

  const validateChaseForm = () => {
    if (!values?.forename) {
      toast.error("Please enter First Name");
      return false;
    } else if (!values?.surname) {
      toast.error("Please enter Surname");
      return false;
    } else if (!values?.email) {
      toast.error("Please enter Email");
      return false;
    } else if (!values?.telephone) {
      toast.error("Please enter Telephone");
      return false;
    } else if (!values?.policyReferenceNumber) {
      toast.error("Please enter Policy Reference Number");
      return false;
    } else if (!values?.policyType) {
      toast.error("Please Select Policy Type");
      return false;
    } else if (!values?.brandCode) {
      toast.error("Please Select Brand");
      return false;
    } else {
      return true;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-20 w-full border-b border-edge bg-surface">
        <AppBar title="DocuSure" profileInfo />
      </div>

      <div className="mx-auto w-full max-w-[880px] animate-fade-in px-4 pb-10 pt-6 sm:px-6">
        {/* ------------------------------ PAGE HEADER ------------------------------ */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-2 rounded-lg px-2 py-1 text-[13px] font-medium text-light-gray transition-colors duration-200 hover:bg-overlay-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-[12px]" />
          Back
        </button>

        <header className="mb-5 flex flex-col gap-1.5">
          <h1 className="text-[22px] font-bold leading-tight text-text-primary">
            Create Chase
          </h1>
          <p className="text-[13px] font-medium text-text-secondary">
            Add a customer and their policy to start a renewal chase.
          </p>
        </header>

        {/* -------------------------------- FORM ---------------------------------- */}
        <div className="rounded-2xl border border-edge bg-surface shadow-card">
          <div className="flex flex-col gap-5 px-5 py-5 sm:px-6">
            <FormSection
              title="Customer"
              description="Who the renewal notices are sent to."
            >
              <FormField>
                <CustomInput
                  autoComplete="none"
                  label="Fore Name"
                  defaultValue={values?.forename}
                  name="forename"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  autoComplete="none"
                  label="Surname"
                  defaultValue={values?.surname}
                  name="surname"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Email Address"
                  autoComplete="none"
                  defaultValue={values?.email}
                  name="email"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Phone Number"
                  autoComplete="none"
                  defaultValue={values?.telephone}
                  name="telephone"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Date of Birth"
                  autoComplete="none"
                  defaultValue={values?.dateOfBirth}
                  name="dateOfBirth"
                  type="date"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Post Code"
                  autoComplete="none"
                  defaultValue={values?.postcode}
                  name="postcode"
                  className="uppercase"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField span={2}>
                <CustomInput
                  label="Address 1"
                  autoComplete="none"
                  defaultValue={values?.address}
                  name="address"
                  onChange={HandleChangeValues}
                />
              </FormField>
            </FormSection>

            <FormSection
              title="Policy"
              description="Reference, cover and renewal details."
            >
              <FormField>
                <CustomInput
                  label="Policy Reference Number"
                  autoComplete="none"
                  defaultValue={values?.policyReferenceNumber}
                  name="policyReferenceNumber"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Policy Number"
                  autoComplete="none"
                  defaultValue={values?.policyNumber}
                  name="policyNumber"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomSelect
                  comboboxType="select"
                  label="Chase Status"
                  labelProperty="name"
                  showTop
                  valueProperty="name"
                  options={loanStatusData}
                  onSingleSelectChange={(option) =>
                    setValues((prev) => ({ ...prev, status: option?.name }))
                  }
                  value={loanStatusData.find(
                    (type) => type.name === values?.status
                  )}
                />
              </FormField>
              <FormField>
                <CustomSelect
                  comboboxType="select"
                  label="Brand"
                  labelProperty="label"
                  showTop
                  valueProperty="name"
                  options={branddata}
                  onSingleSelectChange={(option) =>
                    setValues((prev) => ({ ...prev, brandCode: option?.name }))
                  }
                  value={branddata.find(
                    (type) => type.name === values?.brandCode
                  )}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Registration Number"
                  autoComplete="none"
                  defaultValue={values?.registrationNumber}
                  name="registrationNumber"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Insurer Name"
                  autoComplete="none"
                  defaultValue={values?.insurer}
                  name="insurer"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Renewal Date"
                  autoComplete="none"
                  defaultValue={values?.renewalDate}
                  name="renewalDate"
                  type="date"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Agent Code"
                  autoComplete="none"
                  defaultValue={values?.agentCode}
                  name="agentCode"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField span={2}>
                <RadioField label="Policy Type">
                  <CustomRadioButton
                    id="Nonstandard"
                    name="policyType"
                    label="Standard"
                    value="Standard"
                    checked={values?.policyType === "Standard"}
                    onChange={HandleChangeValues}
                  />
                  <CustomRadioButton
                    id="standard"
                    name="policyType"
                    label="Non-Standard"
                    checked={values?.policyType === "Non-Standard"}
                    onChange={HandleChangeValues}
                    value="Non-Standard"
                  />
                </RadioField>
              </FormField>
            </FormSection>

            <FormSection
              title="Payment &amp; Renewal"
              description="How the customer pays and whether cover renews automatically."
            >
              <FormField>
                <RadioField label="Payment Type">
                  <CustomRadioButton
                    id="cash"
                    name="paymentType"
                    label="Cash"
                    value="cash"
                    checked={values?.paymentType === "cash"}
                    onChange={HandleChangeValues}
                  />
                  <CustomRadioButton
                    id="instalments"
                    name="paymentType"
                    label="Instalments"
                    checked={values?.paymentType === "instalments"}
                    onChange={HandleChangeValues}
                    value="instalments"
                  />
                </RadioField>
              </FormField>
              <FormField>
                <RadioField label="Auto Renew">
                  <CustomRadioButton
                    id="auto-true"
                    name="auto"
                    label="True"
                    value="True"
                    checked={values?.auto === true}
                    onChange={() => {
                      setValues((prev) => ({ ...prev, auto: true }));
                    }}
                  />
                  <CustomRadioButton
                    id="auto-false"
                    name="auto"
                    label="False"
                    checked={values?.auto === false}
                    onChange={() => {
                      setValues((prev) => ({ ...prev, auto: false }));
                    }}
                    value="false"
                  />
                </RadioField>
              </FormField>
              <FormField>
                <RadioField label="Valid Old Payments">
                  <CustomRadioButton
                    id="validOldPaymentDetails-true"
                    name="validOldPaymentDetails"
                    label="True"
                    value="true"
                    checked={values?.validOldPaymentDetails === true}
                    onChange={() => {
                      setValues((prev) => ({
                        ...prev,
                        validOldPaymentDetails: true,
                      }));
                    }}
                  />
                  <CustomRadioButton
                    id="validOldPaymentDetails-false"
                    name="validOldPaymentDetails"
                    label="False"
                    checked={values?.validOldPaymentDetails === false}
                    onChange={() => {
                      setValues((prev) => ({
                        ...prev,
                        validOldPaymentDetails: false,
                      }));
                    }}
                    value="false"
                  />
                </RadioField>
              </FormField>
            </FormSection>
          </div>

          {/* ------------------------------- FOOTER -------------------------------- */}
          <div className="sticky bottom-0 z-10 flex flex-col gap-3 rounded-b-2xl border-t border-edge bg-surface-elevated px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
            <CustomButton
              variant="secondary"
              disabled={loading}
              onClick={() => navigate(-1)}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              loading={loading}
              disabled={loading}
              onClick={createChase}
            >
              Create Chase
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateChase;
